import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import ExportarPDF from '../components/ExportarPDF';
import AdminEditRegistroForm from '../components/AdminEditRegistroForm';
import { formatearNumeroCL } from '../utils/formatUtils';
import { rendimientoSegunColor } from '../utils/rendimientoUtils';
import { showConfirm, showError } from '../lib/swal';

export default function Registros() {
  const user = useAuthStore(state => state.user);

  const [registros, setRegistros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [toners, setToners] = useState([]);
  const [tiposToner, setTiposToner] = useState([]);
  const [personalMap, setPersonalMap] = useState({});

  // Cargar fechas de despacho desde localStorage (solo visual)
  const [fechasDespacho, setFechasDespacho] = useState(() => {
    try {
      const saved = localStorage.getItem('fechasDespacho');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Formatear fecha sin problema de zona horaria
  const formatearFechaSinZonaHoraria = (fechaStr) => {
    if (!fechaStr) return '';
    const [year, month, day] = fechaStr.split('-');
    return `${day}-${month}-${year}`;
  };

  const [filtroSerie, setFiltroSerie] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroToner, setFiltroToner] = useState('');
  const [filtroTipoToner, setFiltroTipoToner] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [filtroLimite, setFiltroLimite] = useState('');

  const [observacionModal, setObservacionModal] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [busqueda, setBusqueda] = useState('');
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [registroEditando, setRegistroEditando] = useState(null);

  const fetchEmails = async (ids) => {
    const { data, error } = await supabase.rpc('obtener_emails', { ids });
    if (error) {
      console.error('Error al obtener emails:', error.message);
      return [];
    }
    return data;
  };

  const fetchInicial = async () => {
    // Primero cargar el personal para crear el mapa
    const { data: personalData } = await supabase
      .from('personal_empresa')
      .select('id, nombre')
      .order('nombre');
    
    const personalMapTemp = {};
    (personalData || []).forEach(persona => {
      personalMapTemp[persona.id] = persona.nombre;
    });
    setPersonalMap(personalMapTemp);

    const { data: regs, error: errR } = await supabase
      .from('registros')
      .select(`
        id,
        fecha,
        guia,
        serie_maquina,
        modelo_maquina,
        contador_actual,
        ultimo_contador,
        observaciones,
        aprobado_por,
        entregado_por,
        cliente_id,
        modelo_toner_id,
        clientes:cliente_id ( nombre ),
        toners:modelo_toner_id ( modelo, rendimiento, rendimiento_color ),
        tipos_toner_instalado:tipo_toner_instalado_id ( nombre ),
        tipo_toner_instalado_id,
        color
      `)
      .order('fecha', { ascending: false });

    if (errR) {
      console.error('Error al cargar registros:', errR);
      return;
    }

    const aprobadoresIds = regs.map(r => r.aprobado_por).filter(Boolean);
    const emails = await fetchEmails(aprobadoresIds);

    // Procesar registros para obtener tipo anterior
    const registrosConEmail = regs.map((r, index) => {
      // Buscar el registro anterior para la misma máquina (serie)
      const registroAnterior = regs.find((anterior, anteriorIndex) => 
        anteriorIndex > index && // Registro más antiguo (índice mayor porque están ordenados por fecha desc)
        anterior.serie_maquina === r.serie_maquina
      );

      return {
        ...r,
        aprobado_por_email: emails.find(e => e.id === r.aprobado_por)?.email || '-',
        entregado_por_nombre: personalMapTemp[r.entregado_por] || '-',
        tipo_anterior: registroAnterior?.tipos_toner_instalado?.nombre || '-'
      };
    });

    setRegistros(registrosConEmail);

    const { data: cls } = await supabase.from('clientes').select('id, nombre');
    setClientes(cls || []);

    const { data: tns } = await supabase.from('toners').select('id, modelo');
    setToners(tns || []);

    const { data: tiposTns } = await supabase.from('tipos_toner').select('id, nombre').order('nombre');
    setTiposToner(tiposTns || []);
  };

  useEffect(() => {
    fetchInicial();
  }, []);

  const terminoBusqueda = busqueda.trim().toLowerCase();
  const registrosFiltrados = registros
    .filter(r => {
      // Búsqueda global: cliente, serie, modelo de tóner y tipo a la vez
      if (!terminoBusqueda) return true;
      return (
        (r.clientes?.nombre || '').toLowerCase().includes(terminoBusqueda) ||
        (r.serie_maquina || '').toLowerCase().includes(terminoBusqueda) ||
        (r.toners?.modelo || '').toLowerCase().includes(terminoBusqueda) ||
        (r.tipos_toner_instalado?.nombre || '').toLowerCase().includes(terminoBusqueda)
      );
    })
    .filter(r => {
      if (fechaInicio && fechaFin) {
        const f = new Date(r.fecha);
        if (f < new Date(fechaInicio) || f > new Date(fechaFin)) return false;
      }
      return true;
    })
    .filter(r => filtroCliente ? r.clientes?.nombre?.toLowerCase().includes(filtroCliente.toLowerCase()) : true)
    .filter(r => filtroSerie ? r.serie_maquina.toLowerCase().includes(filtroSerie.toLowerCase()) : true)
    .filter(r => filtroToner ? r.toners?.modelo === filtroToner : true)
    .filter(r => filtroTipoToner ? r.tipos_toner_instalado?.nombre === filtroTipoToner : true);

  const withLimit = filtroLimite
    ? registrosFiltrados.slice(0, parseInt(filtroLimite))
    : registrosFiltrados;

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = withLimit.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(withLimit.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [busqueda, filtroSerie, filtroCliente, filtroToner, filtroTipoToner, fechaInicio, fechaFin, filtroLimite]);

  const eliminarRegistro = async (id) => {
    const confirmado = await showConfirm(
      '¿Eliminar este registro?',
      'Esta acción no se puede deshacer.',
      { peligro: true }
    );
    if (confirmado) {
      const { error } = await supabase.from('registros').delete().eq('id', id);
      if (!error) {
        setRegistros(prev => prev.filter(r => r.id !== id));
      } else {
        showError('No se pudo eliminar el registro', 'Revisa tu conexión e intenta nuevamente.');
      }
    }
  };

  const hasActiveFilters = fechaInicio || fechaFin || filtroCliente || filtroToner || filtroTipoToner || filtroSerie || filtroLimite;

  const clearFilters = () => {
    setBusqueda('');
    setFechaInicio('');
    setFechaFin('');
    setFiltroCliente('');
    setFiltroToner('');
    setFiltroTipoToner('');
    setFiltroSerie('');
    setFiltroLimite('');
    setCurrentPage(1);
  };

  return (
    <div className="ml-0 lg:ml-72 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-6 min-h-screen animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gradient">Registros de Cambios de Tóner</span>
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {withLimit.length} {withLimit.length === 1 ? 'registro encontrado' : 'registros encontrados'}
            </p>
          </div>
          <ExportarPDF
            registros={withLimit}
            filtros={{
              cliente: filtroCliente,
              serie: filtroSerie,
              toner: filtroToner,
              rango: fechaInicio && fechaFin ? { inicio: fechaInicio, fin: fechaFin } : null,
              limite: filtroLimite,
            }}
          />
        </div>
      </div>

      {/* Buscador principal */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            placeholder="Buscar por cliente, serie, modelo de tóner o tipo..."
            className="w-full pl-12 pr-10 py-3.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white text-base"
          />
          {busqueda && (
            <button
              onClick={() => { setBusqueda(''); setCurrentPage(1); }}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              title="Limpiar búsqueda"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 px-1">
          <span className="text-sm text-gray-500">
            {registrosFiltrados.length} {registrosFiltrados.length === 1 ? 'registro' : 'registros'}
            {(terminoBusqueda || hasActiveFilters) ? ' encontrados' : ' en total'}
          </span>
          <button
            onClick={() => setMostrarFiltrosAvanzados(v => !v)}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {mostrarFiltrosAvanzados ? 'Ocultar filtros avanzados' : 'Filtros avanzados'}
            {hasActiveFilters && !mostrarFiltrosAvanzados && (
              <span className="ml-1 w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filtros */}
      {mostrarFiltrosAvanzados && (
      <div className="bg-white shadow-xl rounded-2xl p-4 sm:p-6 mb-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-sky-100 to-blue-100 rounded-lg">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Filtros de Búsqueda</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="space-y-4">
          {/* Primera fila de filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Desde</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hasta</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cliente</label>
              <input
                type="text"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                placeholder="Buscar cliente..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Modelo Tóner</label>
              <select
                value={filtroToner}
                onChange={(e) => setFiltroToner(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              >
                <option value="">Todos</option>
                {toners.map((t) => (
                  <option key={t.id} value={t.modelo}>
                    {t.modelo}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Últimos</label>
              <select
                value={filtroLimite}
                onChange={(e) => setFiltroLimite(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              >
                <option value="">Todos</option>
                <option value="5">5 movimientos</option>
                <option value="10">10 movimientos</option>
                <option value="20">20 movimientos</option>
              </select>
            </div>
          </div>

          {/* Segunda fila de filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Tóner</label>
              <select
                value={filtroTipoToner}
                onChange={(e) => setFiltroTipoToner(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              >
                <option value="">Todos los tipos</option>
                {tiposToner.map((tipo) => (
                  <option key={tipo.id} value={tipo.nombre}>
                    {tipo.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Número de Serie</label>
              <input
                type="text"
                placeholder="Filtrar por número de serie..."
                value={filtroSerie}
                onChange={(e) => setFiltroSerie(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
              />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Tabla de registros */}
      <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 hidden md:block">
        {currentItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-gray-200 to-gray-100 border-b-2 border-gray-300">
                <tr>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-indigo-700 uppercase tracking-wider border-l-2 border-indigo-200" colSpan="2">
                    Marca de Tóner
                  </th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider border-l-2 border-gray-200"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  {user?.role === "admin" && (
                    <th className="px-3 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider"></th>
                  )}
                </tr>
                <tr className="bg-gradient-to-r from-gray-200 to-gray-100">
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Fecha</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Guía</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Cliente</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Modelo</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Serie</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Tóner</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Color</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-l-2 border-indigo-200">Anterior</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Despachado</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-l-2 border-gray-200">Último</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actual</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Diferencia</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Obs.</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Estado</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Aprobado</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Entregado</th>
                  <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Será Despachado</th>
                  {user?.role === "admin" && (
                    <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {currentItems.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-gradient-to-r hover:from-sky-50 hover:to-blue-50 transition-all duration-200"
                  >
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-900">
                      {new Date(r.fecha).toLocaleDateString('es-CL')}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {r.guia || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                      {r.clientes?.nombre || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {r.modelo_maquina || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-mono text-gray-900">
                      {r.serie_maquina || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {r.toners?.modelo || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {r.color ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {r.color}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs border-l-2 border-indigo-100">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {r.tipo_anterior}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {r.tipos_toner_instalado?.nombre || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-gray-900 border-l-2 border-gray-100">
                      {formatearNumeroCL(r.ultimo_contador || 0)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-medium text-gray-900">
                      {formatearNumeroCL(r.contador_actual || 0)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs font-bold text-indigo-600">
                      {formatearNumeroCL(r.contador_actual - r.ultimo_contador)}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {r.observaciones ? (
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 text-xs font-medium"
                          onClick={() => setObservacionModal(r.observaciones)}
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Ver
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {(() => {
                        const dif = (r.contador_actual || 0) - (r.ultimo_contador || 0);
                        const rend = rendimientoSegunColor(r.toners, r.color);
                        if (rend === 0) return <span className="text-gray-400">-</span>;
                        const margen = rend * 0.1;
                        if (dif < rend - margen)
                          return (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Bajo
                            </span>
                          );
                        if (dif > rend + margen)
                          return (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Alto
                            </span>
                          );
                        return (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            OK
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {r.aprobado_por_email}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-700">
                      {r.entregado_por_nombre}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-xs">
                      {fechasDespacho[`reg_${r.id}`] ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatearFechaSinZonaHoraria(fechasDespacho[`reg_${r.id}`])}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    {user?.role === "admin" && (
                      <td className="px-3 py-3 whitespace-nowrap text-xs">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setRegistroEditando(r);
                              setEditModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-all duration-200"
                            title="Editar registro"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => eliminarRegistro(r.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200"
                            title="Eliminar registro"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="p-4 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full mb-4">
              <svg className="w-12 h-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No se encontraron registros</h3>
            <p className="text-gray-600 text-center max-w-md">
              Intenta ajustar los filtros de búsqueda o verifica que existan registros en el sistema
            </p>
          </div>
        )}
      </div>

      {/* Vista de tarjetas — solo en móvil */}
      <div className="md:hidden space-y-3">
        {currentItems.length === 0 ? (
          <div className="bg-white shadow-lg rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-12 px-4">
            <div className="p-4 bg-gradient-to-br from-sky-100 to-blue-100 rounded-full mb-4">
              <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">No se encontraron registros</h3>
            <p className="text-sm text-gray-600 text-center">Intenta ajustar los filtros de búsqueda</p>
          </div>
        ) : (
          currentItems.map((r) => {
            const diferencia = (r.contador_actual || 0) - (r.ultimo_contador || 0);
            const rend = rendimientoSegunColor(r.toners, r.color);
            let estado = null;
            if (rend > 0) {
              const margen = rend * 0.1;
              if (diferencia < rend - margen) estado = { texto: 'Bajo', clases: 'bg-red-100 text-red-800' };
              else if (diferencia > rend + margen) estado = { texto: 'Alto', clases: 'bg-yellow-100 text-yellow-800' };
              else estado = { texto: 'OK', clases: 'bg-green-100 text-green-800' };
            }

            return (
              <div key={r.id} className="bg-white shadow-lg rounded-2xl border border-gray-100 p-4">
                {/* Encabezado: cliente + estado */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 text-sm leading-snug break-words">
                      {r.clientes?.nombre || 'Sin cliente'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {new Date(r.fecha).toLocaleDateString('es-CL')}
                      {r.guia ? ` · Guía ${r.guia}` : ''}
                    </p>
                  </div>
                  {estado && (
                    <span className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${estado.clases}`}>
                      {estado.texto}
                    </span>
                  )}
                </div>

                {/* Máquina y tóner */}
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Máquina</dt>
                    <dd className="text-gray-800 text-right break-words">
                      {r.modelo_maquina || '—'}
                      {r.serie_maquina && (
                        <span className="block font-mono text-xs text-gray-500">{r.serie_maquina}</span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Tóner</dt>
                    <dd className="text-right">
                      <span className="text-gray-800">{r.toners?.modelo || '—'}</span>
                      {r.color && (
                        <span className="ml-1.5 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {r.color}
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <dt className="text-gray-500 flex-shrink-0">Marca instalada</dt>
                    <dd className="text-right">
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        {r.tipos_toner_instalado?.nombre || '—'}
                      </span>
                    </dd>
                  </div>
                </dl>

                {/* Contadores */}
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-100 text-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Último</p>
                    <p className="text-sm font-medium text-gray-900">{formatearNumeroCL(r.ultimo_contador || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Actual</p>
                    <p className="text-sm font-medium text-gray-900">{formatearNumeroCL(r.contador_actual || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500">Diferencia</p>
                    <p className="text-sm font-bold text-indigo-600">{formatearNumeroCL(diferencia)}</p>
                  </div>
                </div>

                {/* Observaciones y acciones */}
                {(r.observaciones || user?.role === 'admin') && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                    {r.observaciones && (
                      <button
                        onClick={() => setObservacionModal(r.observaciones)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-all duration-200"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Ver observación
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-wrap justify-center items-center gap-2 px-4">
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700 transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>

          {/* Mostrar solo páginas relevantes para evitar overflow */}
          {(() => {
            const maxVisiblePages = 5;
            let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
            let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

            if (endPage - startPage + 1 < maxVisiblePages) {
              startPage = Math.max(1, endPage - maxVisiblePages + 1);
            }

            const pages = [];

            // Primera página si no está visible
            if (startPage > 1) {
              pages.push(
                <button
                  key={1}
                  onClick={() => handlePageChange(1)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 transition-all duration-200"
                >
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(
                  <span key="ellipsis1" className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
            }

            // Páginas visibles
            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`w-10 h-10 flex items-center justify-center font-medium rounded-lg transition-all duration-200 ${
                    currentPage === i
                      ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg transform scale-110'
                      : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                  }`}
                >
                  {i}
                </button>
              );
            }

            // Última página si no está visible
            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(
                  <span key="ellipsis2" className="px-2 text-gray-400">
                    ...
                  </span>
                );
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => handlePageChange(totalPages)}
                  className="w-10 h-10 flex items-center justify-center bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 transition-all duration-200"
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:border-blue-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:text-gray-700 transition-all duration-200"
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Modal de observaciones */}
      {observacionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header del Modal */}
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">Observaciones</h3>
              </div>
            </div>

            {/* Body del Modal */}
            <div className="p-6">
              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{observacionModal}</p>
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setObservacionModal(null)}
                className="px-6 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-[95%] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200">
            <AdminEditRegistroForm
              initialData={registroEditando}
              onSave={async (updated) => {
                const { error } = await supabase
                  .from('registros')
              .update({
                fecha: updated.fecha,
                guia: updated.guia,
                cliente_id: updated.cliente_id,
                modelo_maquina: updated.modelo_maquina,
                serie_maquina: updated.serie_maquina,
                modelo_toner_id: updated.modelo_toner_id,
                tipo_toner_instalado_id: updated.tipo_toner_instalado_id,
                color: updated.color,
                contador_actual: updated.contador_actual,
                aprobado_por: updated.aprobado_por,
                entregado_por: updated.entregado_por,
                observaciones: updated.observaciones || null
              })
                  .eq('id', updated.id);
                if (!error) {
                  // Detectar si las observaciones fueron modificadas y guardar notificación
                  const observacionOriginal = registroEditando.observaciones || '';
                  const observacionEditada = updated.observaciones || '';
                  if (observacionOriginal.trim() !== observacionEditada.trim()) {
                    try {
                      const notificaciones = JSON.parse(localStorage.getItem('notificaciones_observaciones') || '[]');
                      const nuevaNotificacion = {
                        id: `${registroEditando.serie_maquina}_${registroEditando.fecha}_${Date.now()}`,
                        serie_maquina: registroEditando.serie_maquina,
                        cliente_nombre: registroEditando.clientes?.nombre,
                        fecha: registroEditando.fecha,
                        observacion_original: observacionOriginal,
                        observacion_editada: observacionEditada,
                        timestamp: new Date().toISOString(),
                        leida: false
                      };
                      notificaciones.push(nuevaNotificacion);
                      localStorage.setItem('notificaciones_observaciones', JSON.stringify(notificaciones));
                    } catch (err) {
                      console.error('Error al guardar notificación:', err);
                    }
                  }
                  await fetchInicial();
                  setEditModalOpen(false);
                  setRegistroEditando(null);
                } else {
                  showError('No se pudo actualizar el registro', 'Revisa tu conexión e intenta nuevamente.');
                }
              }}
              onClose={() => {
                setEditModalOpen(false);
                setRegistroEditando(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
